<template>
  <div
    class="listing-toolbar-collapse"
    :class="{ 'is-collapsed': collapsed, 'is-expanding': expanding }"
    @transitionend.self="onTransitionEnd"
  >
    <div class="listing-toolbar listing-toolbar--with-filters">
      <!-- The count keeps its box while loading instead of unmounting: it sits
           in front of the filters now, which would jump left on every search. -->
      <p class="listing-toolbar__count" :class="{ 'is-loading': results.loading }">
        <template v-if="mapFilteredParts">
          <template v-for="(part, index) in mapFilteredParts" :key="index">
            <strong v-if="part.bold" class="listing-toolbar__count-value">{{ part.text }}</strong>
            <template v-else>{{ part.text }}</template>
          </template>
          <span class="listing-toolbar__info">
            <button
              type="button"
              class="listing-toolbar__info-button"
              :aria-label="t('listing-toolbar.map-filtered-tooltip')"
            >
              <SvgIcon name="info" :size="16" />
            </button>
            <span class="listing-toolbar__info-tip" role="tooltip">
              {{ t('listing-toolbar.map-filtered-tooltip') }}
            </span>
          </span>
        </template>
        <template v-else>
          <strong class="listing-toolbar__count-value">{{ count }}</strong>
          {{ t(count === 1 ? 'listing-toolbar.result-label' : 'listing-toolbar.results-label') }}
        </template>
      </p>
      <ListingFilterBar :housing-types="housingTypes" />
      <div class="listing-toolbar__views" role="group" :aria-label="t('listing-toolbar.views-aria')">
      <button
        v-for="option in viewOptions"
        :key="option.key"
        type="button"
        class="listing-toolbar__view"
        :class="{ 'is-active': view === option.key }"
        :aria-label="t(option.label)"
        :aria-pressed="view === option.key"
        @click="setView(option.key)"
      >
        <SvgIcon :name="option.icon" :size="16" />
        <span class="listing-toolbar__view-label">{{ t(option.label) }}</span>
      </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import ListingFilterBar from './ListingFilterBar.vue';
import SvgIcon from './SvgIcon.vue';
import { t } from '../lib/i18n';
import { useResults } from '../lib/useResults';
import { useScrollCollapse } from '../lib/useScrollCollapse';
import { useView } from '../lib/useView';

// The filter bar sits inside this row, between the count and the toggles; its
// housing types come from the same bootstrap the page mounts with.
defineProps({
  housingTypes: { type: Array, default: () => [] }
});

const { results } = useResults();
const { view, setView, splitAvailable } = useView();
const { collapsed, expanding, onTransitionEnd } = useScrollCollapse();

const viewOptions = computed(() => [
  { key: 'list', icon: 'list', label: 'listing-toolbar.view.list' },
  ...(splitAvailable.value ? [{ key: 'both', icon: 'grid', label: 'listing-toolbar.view.both' }] : []),
  { key: 'map', icon: 'map', label: 'listing-toolbar.view.map' }
]);

// The headline count is the match on every filter except the map viewport, so it
// does not drop as soon as someone zooms in; totalWithoutBbox is absent without an
// active bbox, in which case total already is that same "all filters" count.
const count = computed(() => results.meta?.totalWithoutBbox ?? results.meta?.total ?? 0);

// totalWithoutBbox is only present (and lower than total's counterpart) once the map
// viewport actually narrows the result set; equal or absent means nothing to report.
// The translated sentence is split on its placeholders so the numbers render bold.
const mapFilteredParts = computed(() => {
  const { total, totalWithoutBbox } = results.meta || {};
  if (totalWithoutBbox === null || totalWithoutBbox === undefined || totalWithoutBbox === total) return null;
  return t('listing-toolbar.map-filtered')
    .split(/(%shown%|%total%)/)
    .filter((part) => part !== '')
    .map((part) => {
      if (part === '%shown%') return { bold: true, text: total };
      if (part === '%total%') return { bold: true, text: totalWithoutBbox };
      return { bold: false, text: part };
    });
});
</script>
